/** Represents a moment with a calendar date and a local time. */
export class TrainMoment {
  /** YYYY-MM-DD */
  date: string;
  /** HH:mm or H:mm */
  time: string;
}

/** Represents one train leg (outbound or inbound). */
export class TrainLeg {
  /** Planned arrival (next-day relative to departure for these services). */
  arrival: TrainMoment;
  /** Planned departure. */
  departure: TrainMoment;
  /** Destination station/terminal (city code for Paris legs, e.g., PLY or PEST). */
  destination: string;
  /** Operator name (e.g., "SNCF"). */
  operator: string;
  /** Origin station/terminal. */
  origin: string;
}

/** Price definition for a passenger segment (adult/child). */
export class Price {
  ageMax?: number;
  ageMin?: number;
  pricePerPerson?: number; //for Couchette
  totalPrice?: number; //for Compartiment
}

/** Root object representing a round trip entry. */
export class TrainSec {
  /** Unique string identifier incremented by 1. */
  id: string;
  /** Return direction (to Paris). Can be undefined for one-direction */
  inbound?: TrainLeg;
  /** Outbound direction. */
  outbound: TrainLeg;
  /** Train data for compartiment */
  compartiment?: TrainData
  /** Train data for couchette */
  couchette?: TrainData
}

export class TrainData {
  /** Service/Prest identifier. */
  prestId: string;
  secondPrestId?: string; // for weekend TNE, we have two trains
  /** Adult pricing for couchette. */
  priceAdult?: Price;
  /** Child pricing for couchette. */
  priceChild?: Price;
  /** Pricing for compartiment (total price for up 6 persons) */
  priceCompartiment?: Price;
  /** Remaining stock for this rotation bundle. */
  stock: number;
  /** When set (next-season TNE), admin unified-berth `stock_override` from DB; omit on legacy trains. */
  stockOverride?: number | null;
  /** Sans hebergement lihe and prest associated */
  liheId: string;
  prestIdAccommodation: string;
}

export enum TrainDirection {
  OUTBOUND = "outbound",
  INBOUND = "inbound"
}

export enum TrainType {
  ROUND_TRIP = "ROUND_TRIP",
  ONE_WAY = "ONE_WAY"
}

export enum BerthType {
  COMPARTIMENT = "Compartiment",
  COUCHETTE = "Couchette"
}

export class TravelskiNightExpressAllocationDto {
    id: number;
    trainId: number;
    trainDate: string;
    origin: string;
    destination: string;
    bookingReference: string;
    externalReference: string;
    firstName: string;
    lastName: string;
    age: number;
    gender: "M" | "F";
    email: string;
    reportEmail: string | null; // Contact email for reporting (used when sameContact = 0)
    channelId: string | null; // Channel ID from booking (e.g., '102' for MMV, '54' or '80' for DJAY)
    phone: string | null;
    birthDate: string;
    type: BerthType;
    allocation: string | null;
    ticketLink: string | null;
    qrcode: string | null;
}

export class TravelskiNightExpressDto {
    id: number;
    journey_id: string;
    trip_direction: TrainDirection;
    trip_type: TrainType;
    origin: string;
    destination: string;
    operator: string | null;
    departure_date: string;
    departure_time: string;
    arrival_date: string;
    arrival_time: string;
    couchette_prest_id: string | null;
    couchette_lihe_id: string | null;
    couchette_prest_id_accommodation: string | null;
    couchette_stock: number | null;
    couchette_price_adult_min_age: number;
    couchette_price_adult_max_age: number;
    couchette_price_adult_per_person: number | null;
    couchette_price_child_min_age: number;
    couchette_price_child_max_age: number;
    couchette_price_child_per_person: number | null;
    compartment_prest_id: string | null;
    compartment_lihe_id: string | null;
    compartment_prest_id_accommodation: string | null;
    compartment_stock: number | null;
    compartment_total_price: number | null;
    created_at: string;
    updated_at: string;
}

export type TicketGenerationRequestDTO = {
    travelskiNightExpress: TravelskiNightExpressDto;
    travelskiNightExpressAllocations: TravelskiNightExpressAllocationDto[];
}

export interface TripLeg {
  departureDate: string;
  departureTime: string;
  departureLocation: string;
  arrivalDate: string;
  arrivalTime: string;
  arrivalLocation: string;
  company: string;
  tripClass: string;
  stock: number;
}

export interface TripData {
  tripId: number;
  transportId: string;
  sessionJourney: string;
  class: string;
  prestId: number | null;
  prestIdOutbound: string | null;
  prestIdInbound: string | null;
  sec: boolean;
  package: boolean;
  platingCarrier: string;
  outbound: TripLeg | null;
  inbound: TripLeg | null;
}

export interface TripsApiResponse {
  success: boolean;
  data: TripData[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}